/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 02/05/2022
 */

import * as React from 'react';
import { LabelCell, Row, ValueCell } from '@/wpt-lib/pdf/common/Table';
import { isNullish } from '@/wpt-lib/utils/isNullish';

const Supervision = ({
    assistantPlannedNumberOfHonoursCandidates,
    assistantPlannedNumberOfMasterResearchCandidates,
    assistantPlannedNumberOfPhDCandidate,
    assistantPlannedFTEOfHonoursCandidates,
    assistantPlannedFTEOfMasterResearchCandidates,
    assistantPlannedFTEOfPhDCandidates,
    CoSupervisoryPlannedNumberOfHonoursCandidates,
    CoSupervisoryPlannedNumberOfMasterResearchCandidates,
    CoSupervisoryPlannedNumberOfPhDCandidates,
    CoSupervisoryPlannedFTEOfHonoursCandidates,
    CoSupervisoryPlannedFTEOfMasterResearchCandidates,
    CoSupervisoryPlannedFTEOfPhDCandidates,
    principalPlannedNumberOfHonoursCandidates,
    principlePlannedNumberOfMasterResearchCandidates,
    principlePlannedNumberOfPhDCandidates,
    principlePlannedFTEOfMasterResearchCandidates,
    principalPlannedFTEOfHonoursCandidates,
    principlePlannedFTEOfPhDCandidates,
    supervisionsAndCompletionsNumberExternalSupervisor,
    supervisionsAndCompletionsFTEExternalSupervisor,
    supervisionsAndCompletionsSupervisoryTraining,
    detailsOfSupervisions,
}: Partial<{
    assistantPlannedNumberOfHonoursCandidates: number;
    assistantPlannedNumberOfMasterResearchCandidates: number;
    assistantPlannedNumberOfPhDCandidate: number;
    assistantPlannedFTEOfHonoursCandidates: number;
    assistantPlannedFTEOfMasterResearchCandidates: number;
    assistantPlannedFTEOfPhDCandidates: number;
    CoSupervisoryPlannedNumberOfHonoursCandidates: number;
    CoSupervisoryPlannedNumberOfMasterResearchCandidates: number;
    CoSupervisoryPlannedNumberOfPhDCandidates: number;
    CoSupervisoryPlannedFTEOfHonoursCandidates: number;
    CoSupervisoryPlannedFTEOfMasterResearchCandidates: number;
    CoSupervisoryPlannedFTEOfPhDCandidates: number;
    principalPlannedNumberOfHonoursCandidates: number;
    principlePlannedNumberOfMasterResearchCandidates: number;
    principlePlannedNumberOfPhDCandidates: number;
    principlePlannedFTEOfMasterResearchCandidates: number;
    principalPlannedFTEOfHonoursCandidates: number;
    principlePlannedFTEOfPhDCandidates: number;
    supervisionsAndCompletionsNumberExternalSupervisor: number;
    supervisionsAndCompletionsFTEExternalSupervisor: number;
    supervisionsAndCompletionsSupervisoryTraining: boolean;
    detailsOfSupervisions: string;
}>) => {
    const fte = [
        assistantPlannedFTEOfHonoursCandidates,
        assistantPlannedFTEOfMasterResearchCandidates,
        assistantPlannedFTEOfPhDCandidates,
        CoSupervisoryPlannedFTEOfHonoursCandidates,
        CoSupervisoryPlannedFTEOfMasterResearchCandidates,
        CoSupervisoryPlannedFTEOfPhDCandidates,
        principlePlannedFTEOfMasterResearchCandidates,
        principalPlannedFTEOfHonoursCandidates,
        principlePlannedFTEOfPhDCandidates,
        supervisionsAndCompletionsNumberExternalSupervisor,
        supervisionsAndCompletionsFTEExternalSupervisor,
    ].reduce((a, c) => a || !isNullish(c), false);

    return (
        <>
            <Row>
                <LabelCell label="Principal Supervisor" />
                <ValueCell single label="PhD" value={principlePlannedNumberOfPhDCandidates ?? 0} />
                <ValueCell single label="Honours" value={principalPlannedNumberOfHonoursCandidates ?? 0} />
                <ValueCell
                    single
                    label="Masters (Research)"
                    value={principlePlannedNumberOfMasterResearchCandidates ?? 0}
                />
            </Row>
            {fte && (
                <Row>
                    <LabelCell label="Principal Supervisor FTE" />
                    <ValueCell single label="PhD FTE" value={principlePlannedFTEOfPhDCandidates ?? 0} />
                    <ValueCell single label="Honours FTE" value={principalPlannedFTEOfHonoursCandidates ?? 0} />
                    <ValueCell
                        single
                        label="Masters (Research) FTE"
                        value={principlePlannedFTEOfMasterResearchCandidates ?? 0}
                    />
                </Row>
            )}
            <Row>
                <LabelCell label="Co-Supervisory Role" />
                <ValueCell single label="PhD" value={CoSupervisoryPlannedNumberOfPhDCandidates ?? 0} />
                <ValueCell single label="Honours" value={CoSupervisoryPlannedNumberOfHonoursCandidates ?? 0} />
                <ValueCell
                    single
                    label="Masters (Research)"
                    value={CoSupervisoryPlannedNumberOfMasterResearchCandidates ?? 0}
                />
            </Row>
            {fte && (
                <Row>
                    <LabelCell label="Co-Supervisory Role FTE" />
                    <ValueCell single label="PhD FTE" value={CoSupervisoryPlannedFTEOfPhDCandidates ?? 0} />
                    <ValueCell single label="Honours FTE" value={CoSupervisoryPlannedFTEOfHonoursCandidates ?? 0} />
                    <ValueCell
                        single
                        label="Masters (Research) FTE"
                        value={CoSupervisoryPlannedFTEOfMasterResearchCandidates ?? 0}
                    />
                </Row>
            )}
            <Row>
                <LabelCell label="Assistant Supervisory Role" />
                <ValueCell single label="PhD" value={assistantPlannedNumberOfPhDCandidate ?? 0} />
                <ValueCell single label="Honours" value={assistantPlannedNumberOfHonoursCandidates ?? 0} />
                <ValueCell
                    single
                    label="Masters (Research)"
                    value={assistantPlannedNumberOfMasterResearchCandidates ?? 0}
                />
            </Row>
            {fte && (
                <Row>
                    <LabelCell label="Assistant Supervisory Role FTE" />
                    <ValueCell single label="PhD FTE" value={assistantPlannedFTEOfPhDCandidates ?? 0} />
                    <ValueCell single label="Honours FTE" value={assistantPlannedFTEOfHonoursCandidates ?? 0} />
                    <ValueCell
                        single
                        label="Masters (Research) FTE"
                        value={assistantPlannedFTEOfMasterResearchCandidates ?? 0}
                    />
                </Row>
            )}

            {fte && (
                <Row>
                    <LabelCell label="External Supervisor" />
                    <ValueCell
                        single
                        label="Candidates"
                        value={supervisionsAndCompletionsNumberExternalSupervisor ?? 0}
                    />
                    <ValueCell single label="FTE" value={supervisionsAndCompletionsFTEExternalSupervisor ?? 0} />
                </Row>
            )}

            {fte && (
                <Row>
                    <ValueCell
                        single
                        label="Completing Supervisory Training"
                        value={supervisionsAndCompletionsSupervisoryTraining ? 'Yes' : 'No'}
                    />
                </Row>
            )}

            {detailsOfSupervisions && (
                <Row>
                    <ValueCell label="Details of Supervisions" value={detailsOfSupervisions} />
                </Row>
            )}
        </>
    );
};

export default Supervision;
